import { useState } from 'react';
import { motion } from 'framer-motion';
import {
  BOOK_PRICE_DISPLAY,
  PAPERBACK_PRICE_DISPLAY,
  PAPERBACK_AMAZON_URL,
  EBOOK_AMAZON_URL,
  KINDLE_SELECT_MODE,
} from '../../lib/constants';
import { useCheckout } from '../../hooks/useCheckout';

const EBOOK_KINDLE_BULLETS = [
  'Complete eBook on Kindle',
  'Reflection prompts and worksheets in every chapter',
  'Read on any Kindle device or the free Kindle app',
  'Syncs your place across devices',
];

const EBOOK_PDF_BULLETS = [
  'Complete eBook (PDF)',
  'Reflection prompts and worksheets in every chapter',
  '72-hour download window',
  'Up to 3 downloads per purchase',
];

export function PricingCTA() {
  const [email, setEmail] = useState('');
  const { checkout, loading, error } = useCheckout();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (email) checkout(email);
  };

  return (
    <section id="pricing" className="py-20 sm:py-28 bg-white">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Pull quote from the back cover */}
        <motion.blockquote
          className="text-center mb-12"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.5 }}
        >
          <p className="font-serif text-2xl sm:text-3xl italic text-text-light leading-relaxed">
            &ldquo;I'm not aiming for a perfect relationship with money,
            <br className="hidden sm:inline" />{' '}
            only a quieter one.&rdquo;
          </p>
        </motion.blockquote>

        <div className="grid md:grid-cols-2 gap-6">
          {/* eBook — purchased on this site */}
          <motion.div
            className="bg-gradient-to-br from-text to-sage-dark rounded-2xl p-8 sm:p-10 text-white flex flex-col"
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: '-100px' }}
            transition={{ duration: 0.5 }}
          >
            <span className="inline-block self-start px-3 py-1 rounded-full bg-white/10 text-sage-light text-xs font-semibold uppercase tracking-wide mb-4">
              eBook
            </span>
            <h2 className="font-serif text-2xl sm:text-3xl font-bold mb-2">
              Read it tonight
            </h2>
            <p className="text-white/80 mb-6">
              {KINDLE_SELECT_MODE
                ? 'Available on Kindle. Read one chapter at a time. That\'s the assignment.'
                : 'Instant PDF download. Read one chapter at a time. That\'s the assignment.'}
            </p>
            <div className="text-4xl font-bold mb-6 font-serif">
              {BOOK_PRICE_DISPLAY}
            </div>
            <ul className="text-left space-y-3 mb-8 text-white/90 text-sm">
              {(KINDLE_SELECT_MODE ? EBOOK_KINDLE_BULLETS : EBOOK_PDF_BULLETS).map((item) => (
                <li key={item} className="flex items-start gap-3">
                  <svg aria-hidden="true" className="w-5 h-5 text-sage-light flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span>{item}</span>
                </li>
              ))}
            </ul>

            {KINDLE_SELECT_MODE ? (
              <>
                <a
                  href={EBOOK_AMAZON_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-auto px-8 py-3 rounded-lg bg-pink text-text font-bold text-center hover:bg-pink-light transition-colors"
                >
                  Get on Kindle for {BOOK_PRICE_DISPLAY}
                </a>
                <p className="mt-4 text-xs text-white/50">
                  You'll be redirected to Amazon to complete your purchase.
                </p>
              </>
            ) : (
              <>
                <form onSubmit={handleSubmit} className="mt-auto flex flex-col gap-3">
                  <label htmlFor="pricing-email" className="sr-only">Email address</label>
                  <input
                    id="pricing-email"
                    type="email"
                    required
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="px-4 py-3 rounded-lg text-text placeholder:text-text-light/50 focus:outline-none focus:ring-2 focus:ring-sage-light transition"
                  />
                  <button
                    type="submit"
                    disabled={loading}
                    className="px-8 py-3 rounded-lg bg-pink text-text font-bold hover:bg-pink-light disabled:opacity-60 transition-colors"
                  >
                    {loading ? 'Loading...' : `Buy eBook for ${BOOK_PRICE_DISPLAY}`}
                  </button>
                </form>
                {error && <p className="mt-3 text-sm text-pink-light">{error}</p>}
                <p className="mt-4 text-xs text-white/50">
                  Secure checkout powered by Square. No account required.
                </p>
              </>
            )}
          </motion.div>

          {/* Paperback — purchased on Amazon (KDP) */}
          <motion.div
            className="bg-bg border border-sage/20 rounded-2xl p-8 sm:p-10 text-text flex flex-col"
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: '-100px' }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <span className="inline-block self-start px-3 py-1 rounded-full bg-sage/10 text-sage-dark text-xs font-semibold uppercase tracking-wide mb-4">
              Paperback
            </span>
            <h2 className="font-serif text-2xl sm:text-3xl font-bold mb-2">
              Hold it in your hands
            </h2>
            <p className="text-text-light mb-6">
              Prefer to write in the margins? Order the print edition from Amazon.
            </p>
            <div className="text-4xl font-bold mb-6 font-serif">
              {PAPERBACK_PRICE_DISPLAY}
            </div>
            <ul className="text-left space-y-3 mb-8 text-text-light text-sm">
              {[
                'Printed paperback edition',
                'Same complete content as the eBook',
                'Ships from Amazon (KDP)',
                'Makes a great gift',
              ].map((item) => (
                <li key={item} className="flex items-start gap-3">
                  <svg aria-hidden="true" className="w-5 h-5 text-sage-dark flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span>{item}</span>
                </li>
              ))}
            </ul>

            <a
              href={PAPERBACK_AMAZON_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-auto px-8 py-3 rounded-lg bg-sage text-white font-bold text-center hover:bg-sage-dark transition-colors"
            >
              Buy Paperback on Amazon for {PAPERBACK_PRICE_DISPLAY}
            </a>
            <p className="mt-4 text-xs text-text-light/60">
              You'll be redirected to Amazon to complete your purchase.
            </p>
          </motion.div>
        </div>

        {/* Free workbook download */}
        <motion.div
          className="mt-8 flex flex-col sm:flex-row items-center justify-between gap-4 bg-sage/8 border border-sage/20 rounded-2xl px-8 py-6"
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.5, delay: 0.15 }}
        >
          <div className="flex items-start gap-4 text-left">
            <span className="text-3xl" aria-hidden="true">📄</span>
            <div>
              <p className="font-semibold text-text text-base">Free Printable Workbook</p>
              <p className="text-sm text-text-light mt-0.5">
                All the quizzes and worksheets from the book — print them out and fill them in at your own pace.
              </p>
            </div>
          </div>
          <a
            href="/money-skills-workbook.pdf"
            download
            className="flex-shrink-0 inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-sage text-white font-semibold hover:bg-sage-dark transition-colors text-sm whitespace-nowrap"
          >
            <svg aria-hidden="true" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
            Download Free
          </a>
        </motion.div>
      </div>
    </section>
  );
}
